import styled from '@emotion/styled';
import Label, { LabelProps } from '~/components/Label/Label';
import { Option } from '~/components/Select';
import capitalize from 'lodash-es/capitalize';
import * as React from 'react';
import { colors } from '~/styles/constants';

interface CommonProps {
  disabled?: boolean;
  htmlFor: string;
  dataAnalytics?: string;
  vertical?: boolean;
}

const OptionsContainer = styled('div')`
  display: flex;

  & > div {
    margin-right: 13px;
  }

  & > div:last-child {
    margin-right: 0;
  }
`;

export const RadioInput = styled('input')`
  margin: 0;
  margin-right: 5px;
  position: relative;
  &:checked::after {
    content: '';
    display: block;
    position: relative;
    background: ${colors.forest700};
    width: 7px;
    height: 7px;
    border: 0;
    border-radius: 50%;
    top: 3px;
    left: 3px;
  }
  &:checked::before {
    content: '';
    display: block;
    position: absolute;
    background: white;
    border: 1px solid ${colors.forest700};
    width: 11px;
    height: 11px;
    border-radius: 50%;
    top: 0px;
    left: 0px;
  }
`;

export const RadioLabel = styled('label')<CommonProps>`
  color: ${(props) => (props.disabled ? colors.form.disabledLabelFontColor : colors.black)};
  padding: ${(props) => (props.vertical ? '8px 0' : '0')};
  cursor: pointer;
`;

export const RadioContainer = styled('div')`
  display: flex;
  align-items: center;
`;

export interface OwnFieldProps extends CommonProps {
  options: Option[];
  invalid?: boolean;
  errorMessage?: string;
  label?: string;
  tabIndex?: number;
  hideErrors?: boolean;
  children?: React.ReactNode;
  labelProps?: LabelProps;
  className?: string;
}

interface RadioTooltipsProps {
  toolTipContent?: React.ReactNode;
}
export interface OwnProps extends OwnFieldProps {
  name: string;
  onChange(option: Option, evt: React.ChangeEvent<HTMLInputElement>): void;
  value: Maybe<Option>;
}

const Radio = (props: OwnProps & RadioTooltipsProps) => {
  const { name, label, onChange, options, value, dataAnalytics, toolTipContent, disabled } = props;

  const onChangeCallback = React.useCallback((evt: React.ChangeEvent<HTMLInputElement>) => {
    const option = options.find((option) => option.value === evt.target.value);

    if (option) return onChange(option, evt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      {label && <Label {...props.labelProps}>{label}</Label>}
      <OptionsContainer
        className={props.className}
        style={
          props.vertical
            ? { flexDirection: 'column', alignItems: 'flex-start' }
            : { flexDirection: 'row', alignItems: 'center' }
        }
      >
        {options?.map((option, idx) => (
          <RadioContainer key={idx}>
            <RadioInput
              type="radio"
              name={name}
              id={option.label as string}
              value={String(option.value)}
              checked={option.value === value?.value}
              disabled={disabled}
              onChange={onChangeCallback}
              data-analytics={
                dataAnalytics && `${dataAnalytics}${capitalize(option.value?.toString())}Input`
              }
            />
            <RadioLabel
              disabled={disabled}
              htmlFor={option.label as string}
              data-analytics={
                dataAnalytics && `${dataAnalytics}${capitalize(option.value?.toString())}Label`
              }
              vertical={props.vertical}
            >
              {option.label}
            </RadioLabel>
            {option.addToolTip && toolTipContent}
          </RadioContainer>
        ))}
      </OptionsContainer>
    </div>
  );
};

export default Radio;
